import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { MapPin, Pencil, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { OnsiteDetailsFields, onsiteFromShift, onsitePayload } from './OnsiteDetailsFields';

const ROWS = [
  ['booth_number', 'Booth / table #'],
  ['reporting_location', 'Reporting location'],
  ['info_desk_location', 'Registration / info desk'],
  ['entrance_instructions', 'Entrance instructions'],
  ['onsite_directions', 'Onsite directions'],
  ['parking_instructions', 'Parking'],
  ['arrival_instructions', 'Arrival instructions'],
];

/**
 * "Onsite / Arrival Details" card for the admin & client shift detail pages.
 * Displays the structured onsite fields Evie reads, and lets authorized
 * users edit them inline. Saving PATCHes (admin) or PUTs (client) the shift.
 */
export const OnsiteArrivalCard = ({ shift, method = 'patch', endpoint, canEdit = true, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(onsiteFromShift(shift));

  const current = onsiteFromShift(shift);
  const plans = current.floor_plan_urls || [];
  const hasAny = ROWS.some(([k]) => current[k]) || plans.length > 0;

  const startEdit = () => { setDraft(onsiteFromShift(shift)); setEditing(true); };
  const change = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = onsitePayload(draft);
      await api[method](endpoint, body);
      toast.success('Onsite details saved. Booked talent were notified of any changes.');
      setEditing(false);
      onSaved && onSaved(body);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save onsite details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="onsite-arrival-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="h-5 w-5 text-blue-600" /> Onsite / Arrival Details
        </CardTitle>
        {canEdit && !editing && (
          <Button variant="outline" size="sm" data-testid="onsite-edit-btn" onClick={startEdit}>
            <Pencil className="h-4 w-4 mr-1" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {shift?.staffing_manager_name && (
          <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3" data-testid="staffing-manager-block">
            <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">iEvntür Staffing Manager</div>
            <div className="text-sm text-gray-900 font-medium" data-testid="staffing-manager-name">
              {shift.staffing_manager_name}{shift.staffing_manager_title ? ` — ${shift.staffing_manager_title}` : ''}
            </div>
            {shift.staffing_manager_phone && (
              <a
                href={`tel:${String(shift.staffing_manager_phone).replace(/[^0-9+]/g, '')}`}
                className="text-sm text-indigo-700 hover:underline"
                data-testid="staffing-manager-phone"
              >
                {shift.staffing_manager_phone}
              </a>
            )}
            <p className="text-xs text-gray-600 mt-1">
              For staffing questions, scheduling issues, releases, or iEvntür support,
              contact {shift.staffing_manager_name} at {shift.staffing_manager_phone}.
            </p>
          </div>
        )}
        {editing ? (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              These details are shown to booked talent and read by Evie to answer
              "where do I go?". Editing them notifies anyone already booked.
            </p>
            <OnsiteDetailsFields value={draft} onChange={change} idPrefix="onsite-edit" />
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving} data-testid="onsite-cancel-btn">
                Cancel
              </Button>
              <Button onClick={save} disabled={saving} data-testid="onsite-save-btn">
                {saving ? 'Saving…' : 'Save details'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {!hasAny && (
              <p className="text-muted-foreground" data-testid="onsite-empty">
                No onsite details captured yet. {canEdit ? 'Tap Edit to add booth #, reporting location, parking, and maps.' : ''}
              </p>
            )}
            {ROWS.map(([k, label]) => (current[k] ? (
              <div key={k} className="flex gap-2" data-testid={`onsite-view-${k}`}>
                <span className="font-medium text-muted-foreground min-w-[150px]">{label}:</span>
                <span className="whitespace-pre-wrap">{current[k]}</span>
              </div>
            ) : null))}
            {plans.length > 0 && (
              <div className="flex gap-2" data-testid="onsite-view-floor_plan_urls">
                <span className="font-medium text-muted-foreground min-w-[150px]">Floor plans / maps:</span>
                <div className="flex flex-col gap-1">
                  {plans.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noreferrer"
                       className="text-blue-600 hover:underline flex items-center gap-1"
                       data-testid={`onsite-view-map-${i}`}>
                      {p.name || p.url} <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OnsiteArrivalCard;
